import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import ContactForm from "@/components/ContactForm";
import { ArrowRight, ShieldCheck, Compass, LineChart, Layers, MonitorSmartphone, Database, Building2 } from "lucide-react";

const IMG_HERO =
  "https://images.pexels.com/photos/2469122/pexels-photo-2469122.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_LITHIUM =
  "https://images.pexels.com/photos/1571442/pexels-photo-1571442.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_VACA =
  "https://images.pexels.com/photos/459728/pexels-photo-459728.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_MINING =
  "https://images.pexels.com/photos/9532213/pexels-photo-9532213.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_DASHBOARD =
  "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=1400";
const IMG_TRANSPARENCY =
  "https://images.pexels.com/photos/7947541/pexels-photo-7947541.jpeg?auto=compress&cs=tinysrgb&w=1400";

export default function Home() {
  const { t, lang } = useI18n();

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = t("meta.title");
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", t("meta.description"));
      const html = document.documentElement;
      if (html) html.setAttribute("lang", lang === "ar" ? "es-AR" : "en");
    }
  }, [lang, t]);

  return (
    <main className="dma-home">
      {/* HERO */}
      <section className="dma-hero" id="top">
        <div className="dma-hero__grid">
          <div className="dma-hero__content">
            <span className="dma-eyebrow">{t("hero.eyebrow")}</span>
            <h1 className="dma-hero__title">{t("hero.title")}</h1>
            <p className="dma-hero__p">{t("hero.paragraph1")}</p>
            <p className="dma-hero__p">{t("hero.paragraph2")}</p>
            <p className="dma-hero__p">{t("hero.paragraph3")}</p>
            <div className="dma-hero__actions">
              <a href="#contact" className="dma-btn dma-btn--primary">
                {t("hero.ctaPrimary")}
                <ArrowRight size={16} />
              </a>
              <a href="#how" className="dma-btn dma-btn--outline">
                {t("hero.ctaSecondary")}
              </a>
            </div>
          </div>
          <div className="dma-hero__visual">
            <div className="dma-hero__image">
              <img src={IMG_HERO} alt={t("hero.imageAlt")} loading="eager" />
              <div className="dma-hero__image-tag">Cordillera de los Andes · Argentina</div>
            </div>
            <div className="dma-hero__stats">
              <div className="dma-stat">
                <span className="dma-stat__value">{t("hero.stat1Value")}</span>
                <span className="dma-stat__label">{t("hero.stat1Label")}</span>
              </div>
              <div className="dma-stat">
                <span className="dma-stat__value">{t("hero.stat2Value")}</span>
                <span className="dma-stat__label">{t("hero.stat2Label")}</span>
              </div>
              <div className="dma-stat">
                <span className="dma-stat__value">{t("hero.stat3Value")}</span>
                <span className="dma-stat__label">{t("hero.stat3Label")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="dma-section" id="sectors">
        <div className="dma-section__head">
          <span className="dma-eyebrow">{t("pillars.eyebrow")}</span>
          <h2 className="dma-section__title">{t("pillars.title")}</h2>
          <p className="dma-section__lead">{t("pillars.subtitle")}</p>
        </div>
        <div className="dma-pillars">
          <article className="dma-pillar">
            <div className="dma-pillar__img">
              <img src={IMG_LITHIUM} alt={t("pillars.item1ImgAlt")} loading="lazy" />
            </div>
            <div className="dma-pillar__body">
              <Layers className="dma-pillar__icon" size={22} />
              <h3>{t("pillars.item1Title")}</h3>
              <p>{t("pillars.item1Text")}</p>
            </div>
          </article>
          <article className="dma-pillar">
            <div className="dma-pillar__img">
              <img src={IMG_VACA} alt={t("pillars.item2ImgAlt")} loading="lazy" />
            </div>
            <div className="dma-pillar__body">
              <Database className="dma-pillar__icon" size={22} />
              <h3>{t("pillars.item2Title")}</h3>
              <p>{t("pillars.item2Text")}</p>
            </div>
          </article>
          <article className="dma-pillar">
            <div className="dma-pillar__img">
              <img src={IMG_MINING} alt={t("pillars.item3ImgAlt")} loading="lazy" />
            </div>
            <div className="dma-pillar__body">
              <Building2 className="dma-pillar__icon" size={22} />
              <h3>{t("pillars.item3Title")}</h3>
              <p>{t("pillars.item3Text")}</p>
            </div>
          </article>
        </div>
      </section>

      {/* WHY */}
      <section className="dma-section dma-section--muted" id="why">
        <div className="dma-section__head">
          <span className="dma-eyebrow">{t("why.eyebrow")}</span>
          <h2 className="dma-section__title">{t("why.title")}</h2>
        </div>
        <div className="dma-why">
          {[
            { Icon: Compass, key: "item1" },
            { Icon: MonitorSmartphone, key: "item2" },
            { Icon: Layers, key: "item3" },
            { Icon: LineChart, key: "item4" },
          ].map(({ Icon, key }) => (
            <div className="dma-why__card" key={key}>
              <span className="dma-why__icon">
                <Icon size={20} />
              </span>
              <h3>{t(`why.${key}Title`)}</h3>
              <p>{t(`why.${key}Text`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section className="dma-section" id="how">
        <div className="dma-how__grid">
          <div className="dma-how__intro">
            <span className="dma-eyebrow">{t("how.eyebrow")}</span>
            <h2 className="dma-section__title">{t("how.title")}</h2>
            <p className="dma-section__lead">{t("how.lead")}</p>
            <div className="dma-how__image">
              <img src={IMG_DASHBOARD} alt={t("how.dashboardAlt")} loading="lazy" />
            </div>
          </div>
          <ol className="dma-how__steps">
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className="dma-how__step">
                <span className="dma-how__num">0{n}</span>
                <div>
                  <h3>{t(`how.step${n}Title`)}</h3>
                  <p>{t(`how.step${n}Text`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="dma-section dma-section--dark" id="transparency">
        <div className="dma-transp">
          <div className="dma-transp__visual">
            <img src={IMG_TRANSPARENCY} alt={t("transparency.imageAlt")} loading="lazy" />
          </div>
          <div className="dma-transp__content">
            <span className="dma-eyebrow dma-eyebrow--light">{t("transparency.eyebrow")}</span>
            <h2 className="dma-section__title dma-section__title--light">{t("transparency.title")}</h2>
            <p>{t("transparency.paragraph1")}</p>
            <p>{t("transparency.paragraph2")}</p>
            <ul className="dma-transp__bullets">
              <li>
                <ShieldCheck size={18} /> {t("transparency.bullet1")}
              </li>
              <li>
                <ShieldCheck size={18} /> {t("transparency.bullet2")}
              </li>
              <li>
                <ShieldCheck size={18} /> {t("transparency.bullet3")}
              </li>
            </ul>
            <a href="#contact" className="dma-btn dma-btn--light">
              {t("transparency.ctaContact")}
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="dma-section" id="contact">
        <div className="dma-contact">
          <div className="dma-contact__head">
            <span className="dma-eyebrow">{t("contact.eyebrow")}</span>
            <h2 className="dma-section__title">{t("contact.title")}</h2>
            <p className="dma-section__lead">{t("contact.subtitle")}</p>
            <p className="dma-contact__alt">
              {t("contact.altContact")}{" "}
              <a href={`mailto:${t("footer.email")}`}>{t("footer.email")}</a>
            </p>
            <div className="dma-contact__meta">
              <div>
                <strong>{t("footer.companyLegalName")}</strong>
                <span>{t("footer.address")}</span>
              </div>
              <div>
                <strong>{t("footer.phone")}</strong>
                <span>{t("footer.jurisdictionValue")}</span>
              </div>
            </div>
          </div>
          <div className="dma-contact__form">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
